import { reportError } from '@/services/error-reporting';
import { fetchBot } from '@/services/services';
import { Alert, Button, Checkbox, Group, List, Modal, Radio, Stack, Text } from '@mantine/core';
import { PatientTrackLabel, Track } from '@bariendo/lib/constants';
import { createReference } from '@medplum/core';
import { Patient, Practitioner } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { ReactElement, useEffect, useState } from 'react';
import { announcePatientTrackChange } from '@/hooks/usePatientTrack';
import { recordTrackSwitchProvenance } from '@/services/trackSwitchProvenance';
import { TRACK_DESTINATIONS, TRACK_SWITCH_ACKNOWLEDGEMENT, trackTransition } from './trackTransition';

const BOT_NAME = 'switch-patient-track';

export interface SwitchTrackModalProps {
  patient: Patient;
  /** The patient's current track, or undefined when it could not be resolved. */
  currentTrack: Track | undefined;
  opened: boolean;
  onClose: () => void;
  /** Called after a successful switch so the caller can re-read the track. */
  onSwitched: () => Promise<void>;
}

/**
 * Picks a new track for a patient, and states what that does before doing it.
 *
 * A track is not cosmetic: it decides whether this patient is ever asked to
 * pay, and moving off `d2c` scrubs their record from the non-HIPAA Close CRM,
 * which cannot be cleanly undone. So the destination is chosen **here**, inside
 * the dialog, rather than from a row-level select — a picker that sits in the
 * same chrome as Service and Location reads as a field edit, and the operator
 * meets the consequences only after committing to one.
 *
 * The consequences and the acknowledgement both come from `trackTransition`, so
 * the copy is derived per transition rather than from a single "is this
 * destructive" boolean. `bariendo-be` re-derives every one of those rules and
 * refuses the call if this disagrees, which is the safe direction.
 */
export function SwitchTrackModal({
  patient,
  currentTrack,
  opened,
  onClose,
  onSwitched,
}: SwitchTrackModalProps): ReactElement {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;

  const [selected, setSelected] = useState<Track | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Reopening must not inherit last time's choice or tick. The acknowledgement
  // is per-switch, and a sticky one would let the second switch through without
  // being read.
  useEffect(() => {
    setSelected(null);
    setAcknowledged(false);
    setError(undefined);
  }, [opened]);

  const transition = selected ? trackTransition(currentTrack, selected) : undefined;
  const needsAcknowledgement = transition?.needsAcknowledgement ?? false;

  async function confirm(): Promise<void> {
    if (!selected || (needsAcknowledgement && !acknowledged)) {
      return;
    }

    setBusy(true);
    setError(undefined);

    try {
      await fetchBot<{ switched: true; toTrack: Track }>(medplum, BOT_NAME, {
        patient: createReference(patient),
        toTrack: selected,
        acknowledgeRetroactivePhiExposure: needsAcknowledgement ? true : undefined,
      });

      // The authenticated Practitioner is known here and nowhere downstream —
      // bariendo-be authenticates with a shared Bearer key and cannot say who
      // called it. Recording attribution as Provenance keeps it in the system
      // that can vouch for it.
      if (profile) {
        try {
          await recordTrackSwitchProvenance(medplum, patient, profile, currentTrack, selected);
        } catch (e) {
          // The track is already switched at this point. Losing the attribution
          // is a smaller failure than telling the operator it did not work and
          // having them try again — a second attempt would be refused by the
          // backend's same-track guard and read as a broken button.
          reportError(e, {
            component: 'SwitchTrackModal',
            extra: { patientId: patient.id, toTrack: selected, reason: 'provenance-write-failed' },
          });
        }
      }

      // Every other reader of the track on the page re-reads it too.
      if (patient.id) announcePatientTrackChange(patient.id);
      await onSwitched();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change the track');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Change this patient's track" centered size="md">
      <Stack>
        {error && <Alert color="red">{error}</Alert>}

        {currentTrack ? (
          <Text size="sm">
            Currently: <b>{PatientTrackLabel[currentTrack]}</b>
          </Text>
        ) : (
          <Alert color="gray" variant="light" title="No track recorded in Orbit">
            <Text size="sm">
              This patient has no care episode carrying a track, which is normal for a self-pay signup. The backend will
              treat them as Self-pay, so a move to a partner track is warned and acknowledged as if it were.
            </Text>
          </Alert>
        )}

        <Radio.Group
          label="Move them to"
          value={selected ?? ''}
          onChange={(value) => {
            setSelected(value as Track);
            setAcknowledged(false);
          }}
        >
          <Stack gap="xs" mt="xs">
            {TRACK_DESTINATIONS.map((destination) => {
              const state = destinationState(destination.track, currentTrack);
              return (
                <Radio
                  key={destination.track}
                  value={destination.track}
                  label={destination.label}
                  description={state.description}
                  disabled={state.disabled}
                />
              );
            })}
          </Stack>
        </Radio.Group>

        {transition && (
          <Alert
            color={needsAcknowledgement ? 'orange' : 'blue'}
            title={needsAcknowledgement ? 'This cannot be cleanly undone' : undefined}
          >
            <List size="sm" spacing={4}>
              {transition.effects.map((effect) => (
                <List.Item key={effect}>{effect}</List.Item>
              ))}
            </List>
          </Alert>
        )}

        {needsAcknowledgement && (
          <Checkbox
            checked={acknowledged}
            onChange={(event) => setAcknowledged(event.currentTarget.checked)}
            label={TRACK_SWITCH_ACKNOWLEDGEMENT}
          />
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            color={needsAcknowledgement ? 'orange' : undefined}
            onClick={confirm}
            loading={busy}
            disabled={!selected || (needsAcknowledgement && !acknowledged)}
          >
            Change track
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * Whether a destination can be picked for *this* patient, and the line under it.
 *
 * Every destination is reachable in principle since BE-9, so what is left is
 * per-patient: they are already on it, or their track is unrecorded and this is
 * Self-pay — the backend's `?? d2c` fallback makes that a no-op it would reject
 * as same-track, and offering it invites a 400 for no gain.
 */
function destinationState(track: Track, currentTrack: Track | undefined): { disabled: boolean; description?: string } {
  if (track === currentTrack) {
    return { disabled: true, description: 'Their current track.' };
  }
  if (currentTrack === undefined && track === Track.D2C) {
    return { disabled: true, description: 'With no track recorded, they are already treated as Self-pay.' };
  }
  return { disabled: false };
}
